import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { Alert, Button, ModalActions, Select, TextArea } from '@evogard/ui';
import type { TicketSummary } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

/**
 * Encaminhar — o FORMULÁRIO, sem diálogo próprio.
 *
 * ## Por que ele deixou de ser um modal, em 17/09
 *
 * Ele era `TransferModal` e desenhava o próprio `<Modal>`. Quando as quatro
 * colunas de ícone da mesa viraram um painel de ações, abri-lo dali seria um
 * modal abrindo outro modal: dois véus empilhados, o foco preso no primeiro e o
 * segundo sobre um fundo já escurecido.
 *
 * Agora ele é o conteúdo que o `ActionsModal` põe no lugar da grade de ações,
 * com a seta de voltar no cabeçalho — um diálogo só, do começo ao fim.
 *
 * **Mora na mesa e no detalhe** desde 23/09/2026 — no detalhe dentro de um
 * `Modal`, ao lado de "Mudar situação". Quem decide SE a ação aparece é quem o
 * monta, pela permissão `tickets.tickets:encaminhar` e pela regra de papel do
 * domínio; este formulário não confere de novo, só busca e grava.
 */
export function TransferForm({
  ticket,
  onDone,
}: {
  ticket: Pick<TicketSummary, 'id' | 'code' | 'department'>;
  /** Fecha o painel inteiro — chamado ao gravar e ao cancelar. */
  onDone: () => void;
}) {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const [departmentId, setDepartmentId] = useState(ticket.department?.id ?? '');
  const [assigneeId, setAssigneeId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  /*
   * A lista do PRÓPRIO módulo (23/09/2026), com o piso de encaminhar — e não
   * `platform.listDepartments`, que pede `platform.departments:ver` e deixava
   * o formulário vazio para quem só atende.
   */
  const departments = useQuery({
    queryKey: ['demandas', 'encaminhar', 'setores', context?.currentTenant.id],
    queryFn: async () => {
      const r = await api.tickets.transferDepartments();
      return ok(r, 200, 'Não foi possível carregar os departamentos.');
    },
  });

  /*
   * Só busca com um setor ESCOLHIDO: "Nenhum" (todos que podem responder) não
   * tem uma lista fechada de gente para oferecer — a demanda vai para a fila,
   * como sempre foi.
   */
  const assignees = useQuery({
    queryKey: ['demandas', 'setor-responsaveis', context?.currentTenant.id, departmentId],
    enabled: departmentId !== '',
    queryFn: async () => {
      const r = await api.tickets.listDepartmentAssignees({
        params: { departmentId },
      });
      return ok(r, 200, 'Não foi possível carregar quem pode aderir neste setor.');
    },
  });

  const transfer = useMutation({
    mutationFn: async () => {
      const r = await api.tickets.transfer({
        params: { id: ticket.id },
        body: {
          departmentId: departmentId === '' ? null : departmentId,
          /*
           * Sem setor ou sem escolha de responsável, `null` devolve a demanda
           * à fila do setor novo — o mesmo comportamento de antes. Só entra um
           * id quando a pessoa escolheu alguém da lista do setor de destino.
           */
          assigneeId: departmentId === '' ? null : assigneeId || null,
          note: note.trim() || undefined,
        },
      });
      return ok(r, 200, 'Não foi possível encaminhar.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['demandas'] });
      onDone();
    },
    onError: (e: Error) => setError(e.message),
  });

  const to = departmentId === '' ? null : departmentId;
  /*
   * Setor igual ao de agora SEM escolher responsável é a única forma real de
   * não-ato — o backend recusa exatamente essa combinação (`assigneeId`
   * `undefined`, nunca escolhido). Setor igual com um responsável NOVO é
   * transferência de verdade: "continua no Financeiro, agora com fulano".
   */
  const departmentUnchanged = to === (ticket.department?.id ?? null);
  const noop = departmentUnchanged && assigneeId === '';

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-texto-suave">
        Escolha o setor e, se já souber, o colaborador de lá que vai pegar o ticket. Sem
        escolher ninguém, ele fica na fila do setor esperando alguém aderir.
      </p>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          transfer.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        <Select
          label="Encaminhar para"
          value={departmentId}
          disabled={departments.isLoading || transfer.isPending}
          onChange={(e) => {
            setDepartmentId(e.target.value);
            /* o responsável é DO setor escolhido — trocar de setor sem zerar
               mandaria o id de alguém que pode nem estar no setor novo */
            setAssigneeId('');
          }}
        >
          <option value="">Nenhum — todos que podem responder</option>
          {(departments.data ?? []).map((dep) => (
            <option key={dep.id} value={dep.id}>
              {dep.nome}
            </option>
          ))}
        </Select>

        {/*
         * Só aparece com um setor escolhido — é o que a lista de `assignees`
         * depende para existir. "Nenhum" continua mandando para a fila, sem
         * pessoa nenhuma para escolher.
         */}
        {departmentId !== '' && (
          <Select
            label="Colaborador do setor"
            value={assigneeId}
            disabled={assignees.isLoading || transfer.isPending}
            onChange={(e) => setAssigneeId(e.target.value)}
            help={
              !assignees.isLoading && (assignees.data ?? []).length === 0
                ? 'Ninguém neste setor pode aderir a tickets — ele vai para a fila.'
                : undefined
            }
          >
            <option value="">
              Ninguém — fica na fila do setor, esperando alguém aderir
            </option>
            {(assignees.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.nome}
              </option>
            ))}
          </Select>
        )}

        {/* `TextArea` e não `Field`: a observação é prosa — não cabe numa linha. */}
        <TextArea
          label="Observação da transferência"
          value={note}
          maxLength={2000}
          counter
          rows={3}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
          placeholder="Por que está indo para lá"
          help="Nota interna: o solicitante não vê. Para quem abriu, aparece apenas que o ticket mudou de departamento."
        />

        {/* O botão desabilitado precisa dizer por quê — senão é um clique que
            não responde, e a pessoa tenta de novo até desistir. */}
        {noop && (
          <p className="text-xs text-texto-suave">
            Escolha um departamento diferente do atual, ou um responsável.
          </p>
        )}

        <ModalActions>
          <Button
            type="button"
            variant="discreto"
            size="sm"
            disabled={transfer.isPending}
            onClick={onDone}
          >
            Cancelar
          </Button>
          <Button type="submit" size="sm" loading={transfer.isPending} disabled={noop}>
            Encaminhar
          </Button>
        </ModalActions>
      </form>
    </div>
  );
}
